package com.gastoncantera.intercom.auth

import androidx.compose.foundation.Image
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.input.TextObfuscationMode
import androidx.compose.foundation.text.input.rememberTextFieldState
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedSecureTextField
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.gastoncantera.intercom.APP_DISPLAY_NAME
import com.gastoncantera.intercom.appVersionName
import dev.gitlive.firebase.auth.FirebaseAuthInvalidCredentialsException
import dev.gitlive.firebase.auth.FirebaseAuthInvalidUserException
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import timbrebahia.shared.generated.resources.Res
import timbrebahia.shared.generated.resources.clear
import timbrebahia.shared.generated.resources.logo
import timbrebahia.shared.generated.resources.visibility
import timbrebahia.shared.generated.resources.visibility_off

private const val ATTEMPTS_FOR_RESET = 3
private val BahiaBlue = Color(0xFF0D5DBD)

@Composable
fun LoginScreen(authRepository: AuthRepository) {
    val scope = rememberCoroutineScope()
    val focusManager = LocalFocusManager.current
    var email by remember { mutableStateOf("") }
    val passwordState = rememberTextFieldState()
    var passwordVisible by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var info by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(false) }
    var attempts by remember { mutableIntStateOf(0) }
    var resetRequested by remember { mutableStateOf(false) }

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val screenHeight = maxHeight
        Column(
            modifier = Modifier
                .fillMaxWidth()
                // Mínimo alto = pantalla; si no entra, scroll.
                .heightIn(min = screenHeight)
                .verticalScroll(rememberScrollState())
                .pointerInput(Unit) {
                    detectTapGestures(onTap = { focusManager.clearFocus() })
                },
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    // Reserva espacio abajo para la versión; centra el formulario en el resto.
                    .heightIn(min = screenHeight - 40.dp)
                    .padding(horizontal = 16.dp, vertical = 24.dp),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
            BoxWithConstraints(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 24.dp),
                contentAlignment = Alignment.TopCenter,
            ) {
                val logoWidth = when {
                    maxWidth <= 480.dp -> maxWidth * 0.8f
                    else -> (maxWidth * 0.25f).coerceIn(200.dp, 280.dp)
                }
                Box(
                    modifier = Modifier
                        .width(logoWidth)
                        .aspectRatio(560f / 531f),
                    contentAlignment = Alignment.Center,
                ) {
                    Image(
                        painter = painterResource(Res.drawable.logo),
                        contentDescription = APP_DISPLAY_NAME,
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Fit,
                    )
                }
            }
        Text(
            "Iniciar sesión",
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 12.dp),
            textAlign = TextAlign.Center,
            style = MaterialTheme.typography.titleMedium,
        )

        OutlinedTextField(
            value = email,
            onValueChange = { email = it },
            label = { Text("Email") },
            singleLine = true,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Email,
                imeAction = ImeAction.Next,
            ),
            trailingIcon = {
                if (email.isNotEmpty()) {
                    IconButton(
                        onClick = { email = "" },
                        modifier = Modifier.focusProperties { canFocus = false },
                    ) {
                        Icon(
                            painter = painterResource(Res.drawable.clear),
                            contentDescription = "Borrar email",
                        )
                    }
                }
            },
            modifier = Modifier.fillMaxWidth(),
        )

        OutlinedSecureTextField(
            state = passwordState,
            label = { Text("Contraseña") },
            textObfuscationMode = if (passwordVisible) {
                TextObfuscationMode.Visible
            } else {
                TextObfuscationMode.Hidden
            },
            keyboardOptions = KeyboardOptions(
                keyboardType = if (passwordVisible) {
                    KeyboardType.PasswordVisible
                } else {
                    KeyboardType.Password
                },
                imeAction = ImeAction.Done,
            ),
            onKeyboardAction = { performDefaultAction ->
                performDefaultAction()
                focusManager.clearFocus()
            },
            trailingIcon = {
                IconButton(
                    onClick = { passwordVisible = !passwordVisible },
                    modifier = Modifier.focusProperties { canFocus = false },
                ) {
                    Icon(
                        painter = painterResource(
                            if (passwordVisible) Res.drawable.visibility_off else Res.drawable.visibility,
                        ),
                        contentDescription = if (passwordVisible) {
                            "Ocultar contraseña"
                        } else {
                            "Mostrar contraseña"
                        },
                    )
                }
            },
            modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
        )

        error?.let {
            Text(
                it,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall,
                textAlign = TextAlign.Center,
                modifier = Modifier.padding(top = 8.dp),
            )
        }

        info?.let {
            Text(
                it,
                style = MaterialTheme.typography.bodySmall,
                textAlign = TextAlign.Center,
                modifier = Modifier.padding(top = 8.dp),
            )
        }

        Button(
            onClick = {
                focusManager.clearFocus()
                error = null
                info = null
                if (!email.isValidEmail()) {
                    error = "El email no es válido."
                    return@Button
                }
                loading = true
                scope.launch {
                    try {
                        authRepository.signIn(email, passwordState.text.toString())
                    } catch (e: Throwable) {
                        error = authErrorMessage(e)
                        if (!isAccountDisabled(e)) attempts++
                    } finally {
                        loading = false
                    }
                }
            },
            enabled = !loading && email.isNotBlank() && passwordState.text.isNotBlank(),
            colors = ButtonDefaults.buttonColors(
                containerColor = BahiaBlue,
                contentColor = Color.White,
            ),
            modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
        ) {
            if (loading) CircularProgressIndicator(color = Color.White) else Text("Entrar")
        }

        if (attempts >= ATTEMPTS_FOR_RESET && !resetRequested) {
            TextButton(
                onClick = {
                    focusManager.clearFocus()
                    error = null
                    info = null
                    if (email.isBlank()) {
                        error = "Ingresá tu email para restablecer la contraseña."
                        return@TextButton
                    }
                    if (!email.isValidEmail()) {
                        error = "El email no es válido."
                        return@TextButton
                    }
                    resetRequested = true
                    loading = true
                    scope.launch {
                        try {
                            authRepository.sendPasswordResetEmail(email)
                            info = "Si existe una cuenta con ese email, te enviamos instrucciones " +
                                "para restablecer la contraseña. El mensaje puede llegar a Spam " +
                                "o Correo no deseado."
                            attempts = 0
                        } catch (e: Throwable) {
                            error = resetErrorMessage(e)
                        } finally {
                            resetRequested = false
                            loading = false
                        }
                    }
                },
                enabled = !loading,
                modifier = Modifier.padding(top = 8.dp),
            ) {
                Text("¿Olvidaste tu contraseña?")
            }
            Text(
                text = "El email puede llegar a Spam o Correo no deseado.",
                style = MaterialTheme.typography.bodySmall,
                textAlign = TextAlign.Center,
            )
        }
            }

            appVersionName().takeIf { it.isNotEmpty() }?.let { version ->
                Text(
                    text = "v$version",
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.45f),
                    textAlign = TextAlign.Center,
                    modifier = Modifier
                        .fillMaxWidth()
                        .navigationBarsPadding()
                        .padding(bottom = 8.dp),
                )
            }
        }
    }
}

internal fun String.isValidEmail(): Boolean =
    EMAIL_REGEX.matches(trim())

// Suficiente para UX; Firebase valida de nuevo en el servidor.
private val EMAIL_REGEX = Regex("^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$")

private fun isAccountDisabled(e: Throwable): Boolean {
    val msg = e.message.orEmpty()
    return "USER_DISABLED" in msg ||
        "user-disabled" in msg ||
        "ERROR_USER_DISABLED" in msg ||
        msg.contains("has been disabled", ignoreCase = true) ||
        // iOS AuthErrorCode.userDisabled
        msg.contains("17005")
}

private fun authErrorMessage(e: Throwable): String = when {
    isAccountDisabled(e) -> "Esta cuenta está deshabilitada. Contactá al administrador."
    e is FirebaseAuthInvalidCredentialsException -> "Email o contraseña incorrectos."
    e is FirebaseAuthInvalidUserException -> "No existe una cuenta con ese email."
    else -> "No se pudo iniciar sesión. Revisá tu conexión e intentá de nuevo."
}

internal fun resetErrorMessage(e: Throwable): String = when (e) {
    is FirebaseAuthInvalidCredentialsException -> "El email no es válido."
    else -> "No se pudo enviar el email. Revisá tu conexión e intentá de nuevo."
}
